import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Target, Sparkles, Plus, MapPin, Flag, ListChecks, ShieldCheck,
  AlertTriangle, Check, PenLine, Loader2, Pencil, X, Fingerprint,
  Lock, CalendarClock, Map, ArrowRight, Star,
} from "lucide-react";
import client, { errText } from "../../lib/api";
import { SignaturePad } from "../../components/SignaturePad";
import ConfirmDialog from "../../components/ConfirmDialog";

const QUIT_PHRASE = "I am closing this goal on purpose";
const PRESETS = [4, 8, 12];

function TimelinePicker({ weeks, onPick, testidPrefix = "timeline" }) {
  const isCustom = weeks != null && !PRESETS.includes(Number(weeks));
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={`${testidPrefix}-picker`}>
      {PRESETS.map((w) => (
        <button
          key={w}
          type="button"
          onClick={() => onPick(w)}
          data-testid={`${testidPrefix}-${w}`}
          className="px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5"
          style={{
            background: Number(weeks) === w ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-canvas-2))",
            color: Number(weeks) === w ? "#fff" : "hsl(var(--klareet-ink))",
            borderColor: Number(weeks) === w ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-line-soft))",
          }}
        >
          {w} weeks
        </button>
      ))}
      <label
        className="px-3 py-2 rounded-full text-sm border flex items-center gap-2"
        style={{
          background: isCustom ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-canvas-2))",
          color: isCustom ? "#fff" : "hsl(var(--klareet-ink))",
          borderColor: isCustom ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-line-soft))",
        }}
      >
        <CalendarClock size={13} /> custom
        <input
          type="number"
          min={1}
          max={104}
          value={isCustom ? weeks : ""}
          onChange={(e) => onPick(e.target.value ? Number(e.target.value) : null)}
          placeholder="wks"
          className="w-12 bg-transparent outline-none text-center"
          style={{ color: isCustom ? "#fff" : "hsl(var(--klareet-ink))" }}
          data-testid={`${testidPrefix}-custom-input`}
        />
      </label>
    </div>
  );
}

function StepperNode({ icon, kicker, title, body, tint, last, testid }) {
  return (
    <div className="relative pl-10 pb-6" data-testid={testid}>
      {!last && (
        <div className="absolute left-[15px] top-8 bottom-0 w-0.5" style={{ background: "hsl(var(--klareet-line))" }} />
      )}
      <span
        className="absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center"
        style={{ background: tint || "hsl(var(--klareet-teal) / 0.14)", color: "hsl(var(--klareet-teal))" }}
      >
        {icon}
      </span>
      <div className="k-label mb-1">{kicker}</div>
      {title && <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>{title}</div>}
      <div className="k-quiet !text-sm" style={{ lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function GapAnalysis({ gap }) {
  if (!gap) return null;
  return (
    <div className="mt-6" data-testid="gap-analysis">
      <div className="k-label mb-4 flex items-center gap-2">
        <Sparkles size={13} /> Klair's read on this goal
        <span className="k-quiet !text-[10px] ml-1">grounded on {gap.grounded_on}</span>
      </div>
      <p className="klareet-serif mb-5" style={{ fontSize: "1.05rem", lineHeight: 1.55, color: "hsl(var(--klareet-ink))" }} data-testid="gap-narrative">
        {gap.narrative}
      </p>
      <div>
        <StepperNode icon={<MapPin size={15} />} kicker="where you stand" body={gap.stand} testid="gap-stand" />
        <StepperNode icon={<Flag size={15} />} kicker="how far" body={gap.distance} testid="gap-distance" />
        {(gap.steps || []).map((s, i) => (
          <StepperNode
            key={i}
            icon={<ListChecks size={15} />}
            kicker={s.window}
            title={s.title}
            body={s.detail}
            last={i === (gap.steps.length - 1)}
            testid={`gap-step-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function OffTrackWarning({ reason, acknowledged, onAck, busy }) {
  return (
    <div
      className="mt-4 rounded-xl p-5"
      style={{ background: "hsl(28 90% 96%)", border: "1px solid hsl(28 78% 82%)" }}
      data-testid="offtrack-warning"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="shrink-0 mt-0.5" style={{ color: "hsl(28 74% 45%)" }} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold mb-1" style={{ color: "hsl(28 60% 30%)" }}>
            Heads up, this looks a little off from your read
          </div>
          <p className="!text-sm" style={{ color: "hsl(28 40% 32%)", lineHeight: 1.55 }} data-testid="offtrack-reason">
            {reason}
          </p>
          {acknowledged ? (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "hsl(28 60% 35%)" }} data-testid="offtrack-acknowledged">
              <Check size={13} /> You chose to keep going. That is your call.
            </div>
          ) : (
            <button
              onClick={onAck}
              disabled={busy}
              className="mt-3 px-4 py-2 rounded-full text-sm font-medium transition-all hover:-translate-y-0.5"
              style={{ background: "hsl(28 74% 45%)", color: "#fff" }}
              data-testid="offtrack-ack-btn"
            >
              I know what I am doing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PledgeReceipt({ goal }) {
  const [sigUrl, setSigUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    if (goal?.signature_image_path) {
      client.get(`/engagement/goal/${goal.id}/signature`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); revoke = u; setSigUrl(u); })
        .catch(() => setSigUrl(null));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [goal?.id, goal?.signature_image_path]);

  const when = goal.signed_at ? new Date(goal.signed_at).toLocaleString() : "";
  return (
    <div
      className="mt-5 rounded-xl overflow-hidden"
      style={{ border: "1px solid hsl(var(--klareet-teal) / 0.3)", background: "hsl(var(--klareet-teal) / 0.05)" }}
      data-testid="pledge-receipt"
    >
      <div className="px-5 py-3 flex items-center gap-2" style={{ background: "hsl(var(--klareet-teal) / 0.1)", borderBottom: "1px solid hsl(var(--klareet-teal) / 0.2)" }}>
        <ShieldCheck size={16} style={{ color: "hsl(var(--klareet-teal))" }} />
        <span className="font-semibold text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>Signed pledge, on the record</span>
      </div>
      <div className="p-5 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="k-label mb-1">signed by</div>
          <div className="klareet-serif" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-ink))" }} data-testid="receipt-signer">{goal.signer_name}</div>
          <div className="k-quiet !text-xs mt-1" data-testid="receipt-when">{when}</div>
          {sigUrl && (
            <img
              src={sigUrl}
              alt="your signature"
              className="mt-3 rounded-lg"
              style={{ maxHeight: 72, background: "#fff", border: "1px solid hsl(var(--klareet-line-soft))", padding: 6 }}
              data-testid="receipt-signature-image"
            />
          )}
        </div>
        <div className="rounded-lg p-3" style={{ background: "hsl(var(--klareet-canvas))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-label mb-1 flex items-center gap-1.5"><Fingerprint size={12} /> verification receipt</div>
          <div className="klareet-mono !text-sm font-semibold tracking-wider" style={{ color: "hsl(var(--klareet-ink))" }} data-testid="receipt-fingerprint">
            {goal.signature_fingerprint}
          </div>
          <div className="k-quiet !text-[10px] mt-2 break-all klareet-mono" data-testid="receipt-sha">
            SHA-256: {goal.signature_sha256}
          </div>
          <div className="k-quiet !text-[10px] mt-2 flex items-center gap-1">
            <Lock size={10} /> tamper evident. editing this goal voids the signature.
          </div>
        </div>
      </div>
    </div>
  );
}

function PledgeModule({ goal, onSigned }) {
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [commit, setCommit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const blocked = goal.off_track && !goal.off_track_acknowledged;
  const ready = name.trim().length >= 2 && sig && commit && !blocked;

  const sign = async () => {
    if (!ready) return;
    setBusy(true); setErr("");
    try {
      const { data } = await client.post(`/engagement/goal/${goal.id}/sign`, {
        signer_name: name.trim(),
        signature_image: sig,
        acknowledge_off_track: goal.off_track_acknowledged || false,
      });
      onSigned(data);
    } catch (e) {
      setErr(errText(e, "That did not sign. Please try again."));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="mt-5 k-card"
      style={{ background: "hsl(var(--klareet-canvas-2))" }}
      data-testid="pledge-module"
    >
      <div className="k-label mb-1 flex items-center gap-1.5"><PenLine size={13} /> your pledge</div>
      <p className="k-quiet !text-sm mb-4" style={{ lineHeight: 1.55 }}>
        This is a promise to yourself, not a contract for anyone else. By signing, you own this
        goal and its plan. Klair will keep it here for you and check in as you go.
      </p>

      {blocked && (
        <div className="k-quiet !text-xs mb-3 flex items-center gap-1.5" style={{ color: "hsl(28 60% 40%)" }} data-testid="pledge-blocked-note">
          <AlertTriangle size={12} /> Acknowledge the heads up above before you sign.
        </div>
      )}

      <div className="mb-4">
        <div className="k-quiet !text-xs mb-1.5">your full name</div>
        <input
          className="k-input"
          value={name}
          maxLength={120}
          placeholder="type your full name"
          onChange={(e) => setName(e.target.value)}
          data-testid="pledge-name-input"
        />
      </div>

      <div className="mb-4">
        <SignaturePad onChange={setSig} disabled={blocked} testid="pledge-signature" />
      </div>

      <label className="flex items-start gap-2.5 mb-4 cursor-pointer select-none" data-testid="pledge-commit-label">
        <span
          className="w-5 h-5 rounded-md border shrink-0 mt-0.5 flex items-center justify-center transition-colors"
          style={{
            background: commit ? "hsl(var(--klareet-teal))" : "#fff",
            borderColor: commit ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line))",
          }}
        >
          {commit && <Check size={13} color="#fff" />}
        </span>
        <input type="checkbox" className="sr-only" checked={commit} onChange={(e) => setCommit(e.target.checked)} data-testid="pledge-commit-checkbox" />
        <span className="text-sm" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>
          I commit to this goal and its plan, in my own words and my own time.
        </span>
      </label>

      {err && <div className="k-chip k-chip-warn mb-3" data-testid="pledge-error">{err}</div>}

      <button
        onClick={sign}
        disabled={!ready || busy}
        className="k-btn k-btn-primary"
        data-testid="pledge-sign-btn"
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Signing.</> : <><ShieldCheck size={14} /> Sign my pledge</>}
      </button>
    </div>
  );
}

function GoalRoadmapPreview({ goal, base }) {
  const steps = goal?.gap_analysis?.steps || [];
  if (steps.length === 0) return null;
  return (
    <div className="mt-5 k-card" data-testid="goal-roadmap-preview">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="k-label !mb-0 flex items-center gap-1.5"><Map size={13} /> your roadmap</div>
        <Link to={`${base}/roadmap?goal=${goal.id}`} className="k-btn k-btn-primary !py-1.5 !px-3 !text-xs" data-testid="goal-open-roadmap">
          Open the river <ArrowRight size={12} />
        </Link>
      </div>
      <div className="relative pl-6">
        <div className="absolute left-[7px] top-1 bottom-1 w-0.5" style={{ background: "hsl(var(--klareet-line))" }} />
        {steps.map((s, i) => (
          <div key={i} className="relative pb-3 last:pb-0" data-testid={`preview-stop-${i}`}>
            <span className="absolute -left-[22px] top-0.5 w-3.5 h-3.5 rounded-full" style={{ background: i === 0 ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-canvas-2))", border: "2px solid hsl(var(--klareet-teal))" }} />
            <div className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-muted))" }}>{s.window}</div>
            <div className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{s.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function GoalsTab({ base }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [mode, setMode] = useState("view"); // view | create
  const [draftTitle, setDraftTitle] = useState("");
  const [draftWeeks, setDraftWeeks] = useState(8);
  const [analyzing, setAnalyzing] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editWeeks, setEditWeeks] = useState(8);
  const [err, setErr] = useState("");
  const [pills, setPills] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("klareet.report");
      if (raw) {
        const rep = JSON.parse(raw);
        if (Array.isArray(rep?.goal_pills)) setPills(rep.goal_pills);
      }
    } catch (_e) { /* ignore */ }
  }, []);

  const load = useCallback(async (selectId) => {
    try {
      const { data } = await client.get("/engagement/goal");
      const items = data?.items || [];
      setGoals(items);
      if (selectId) setActiveId(selectId);
      else setActiveId((cur) => cur || (items.length ? items[0].id : cur));
    } catch (_e) { setGoals([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => goals.find((g) => g.id === activeId) || null, [goals, activeId]);
  const activeCount = goals.length;

  const startCreate = () => {
    setErr("");
    setDraftTitle("");
    setDraftWeeks(8);
    setMode("create");
  };

  const createGoal = async () => {
    if (!draftTitle.trim()) return;
    setErr("");
    try {
      const { data } = await client.post("/engagement/goal", {
        title: draftTitle.trim(),
        timeline_weeks: draftWeeks || 8,
      });
      setMode("view");
      await load(data.id);
      analyze(data.id);
    } catch (e) {
      setErr(errText(e, "Could not create that goal."));
    }
  };

  const analyze = async (goalId) => {
    setAnalyzing(true);
    try {
      await client.post(`/engagement/goal/${goalId}/gap-analysis?warm=true`);
      await load(goalId);
    } catch (_e) { /* silent */ }
    finally { setAnalyzing(false); }
  };

  const ackOffTrack = async () => {
    if (!active) return;
    setAckBusy(true);
    try {
      await client.post(`/engagement/goal/${active.id}/acknowledge-off-track`);
      await load(active.id);
    } catch (_e) { /* silent */ }
    finally { setAckBusy(false); }
  };

  const beginEdit = () => {
    setEditTitle(active.title);
    setEditWeeks(active.timeline_weeks || 8);
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      await client.patch(`/engagement/goal/${active.id}`, {
        title: editTitle.trim(),
        timeline_weeks: editWeeks || 8,
      });
      setEditing(false);
      await load(active.id);
      analyze(active.id);
    } catch (e) {
      setErr(errText(e, "Could not save that change."));
    }
  };

  const closeGoal = async () => {
    if (!active) return;
    await client.post(`/engagement/goal/${active.id}/archive`, { quit_phrase: QUIT_PHRASE, reason: "" });
    const remaining = goals.filter((g) => g.id !== active.id);
    setActiveId(remaining[0]?.id || null);
    setConfirmClose(false);
    await load(remaining[0]?.id);
  };

  const onSigned = async (updated) => {
    setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  };

  const setDriving = async (id) => {
    try {
      await client.post(`/engagement/goal/${id}/drive`);
      setGoals((prev) => prev.map((g) => ({ ...g, is_driving: g.id === id })));
    } catch (_e) { /* silent */ }
  };

  if (loading) {
    return <div className="k-card text-center py-12" data-testid="goals-loading"><Loader2 size={22} className="animate-spin mx-auto" style={{ color: "hsl(var(--klareet-muted))" }} /></div>;
  }

  return (
    <div data-testid="goals-tab-root">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="k-label mb-1">my goals</div>
          <h2 className="klareet-serif" style={{ fontSize: "clamp(1.6rem, 3vw, 2.1rem)", letterSpacing: "-0.01em" }}>
            What you're working toward.
          </h2>
        </div>
        <button
          onClick={startCreate}
          disabled={activeCount >= 3}
          className="k-btn k-btn-primary !text-sm disabled:opacity-40"
          data-testid="goals-new-btn"
          title={activeCount >= 3 ? "Close a goal to add a new one" : "Add a goal"}
        >
          <Plus size={14} /> New goal
        </button>
      </div>

      {activeCount >= 3 && mode === "view" && (
        <p className="k-quiet !text-xs mb-4" data-testid="goals-max-note">
          Three active goals is the max, so each one stays a real commitment. Close one to add another.
        </p>
      )}

      {/* Switcher */}
      {(goals.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="goals-switcher">
          {goals.map((g) => (
            <button
              key={g.id}
              onClick={() => { setActiveId(g.id); setMode("view"); setEditing(false); }}
              data-testid={`goal-switch-${g.id}`}
              className="px-4 py-2 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2 max-w-[240px]"
              style={{
                background: activeId === g.id && mode === "view" ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-canvas-2))",
                color: activeId === g.id && mode === "view" ? "#fff" : "hsl(var(--klareet-ink))",
                borderColor: activeId === g.id && mode === "view" ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line-soft))",
              }}
            >
              {g.is_driving ? <Star size={13} fill="currentColor" /> : g.signed ? <ShieldCheck size={13} /> : <Target size={13} />}
              <span className="truncate">{g.title}</span>
            </button>
          ))}
        </div>
      )}

      {err && <div className="k-chip k-chip-warn mb-4" data-testid="goals-error">{err}</div>}

      {/* Create flow */}
      {mode === "create" && (
        <div className="k-card" data-testid="goal-create-card">
          {pills.length > 0 && (
            <div className="mb-5" data-testid="goal-suggested-pills">
              <div className="k-label mb-2 flex items-center gap-1.5"><Sparkles size={13} /> ideas from your read</div>
              <div className="flex flex-wrap gap-2">
                {pills.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setDraftTitle(p)}
                    data-testid={`goal-pill-${i}`}
                    className="px-3.5 py-2 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5 text-left"
                    style={{
                      background: draftTitle === p ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-canvas-2))",
                      color: draftTitle === p ? "#fff" : "hsl(var(--klareet-ink))",
                      borderColor: draftTitle === p ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line-soft))",
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="k-label mb-3">name your goal</div>
          <textarea
            className="k-input klareet-serif"
            rows={2}
            maxLength={200}
            placeholder="what do you want to make happen?"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            style={{ fontSize: "1.3rem", lineHeight: 1.35 }}
            data-testid="goal-title-input"
          />
          <div className="k-label mt-5 mb-3">give it a timeline</div>
          <TimelinePicker weeks={draftWeeks} onPick={setDraftWeeks} testidPrefix="create-timeline" />
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setMode("view")} className="k-btn k-btn-ghost !text-sm" data-testid="goal-create-cancel">Cancel</button>
            <button onClick={createGoal} disabled={!draftTitle.trim()} className="k-btn k-btn-primary !text-sm" data-testid="goal-create-submit">
              <Sparkles size={14} /> Create and analyze
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {mode === "view" && goals.length === 0 && (
        <div className="k-card text-center py-14" data-testid="goals-empty">
          <Target size={30} className="mx-auto mb-3" style={{ color: "hsl(var(--klareet-muted))" }} />
          <p className="k-quiet mb-4">No goals yet. Name one and Klair will read it against your snapshot, then help you commit to it.</p>
          <button onClick={startCreate} className="k-btn k-btn-primary !text-sm" data-testid="goals-empty-new"><Plus size={14} /> Add your first goal</button>
        </div>
      )}

      {/* Goal detail */}
      {mode === "view" && active && (
        <div className="k-card" data-testid="goal-detail">
          {!editing ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <p className="klareet-serif flex-1" style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.4rem)", lineHeight: 1.2, letterSpacing: "-0.01em", color: "hsl(var(--klareet-ink))" }} data-testid="goal-statement">
                  {active.title}
                </p>
                <div className="flex gap-1 shrink-0">
                  <button onClick={beginEdit} className="p-2 rounded-full hover:bg-[hsl(var(--klareet-canvas-2))]" title="Edit goal" data-testid="goal-edit-btn"><Pencil size={15} style={{ color: "hsl(var(--klareet-muted))" }} /></button>
                  <button onClick={() => setConfirmClose(true)} className="p-2 rounded-full hover:bg-[hsl(var(--klareet-canvas-2))]" title="Close goal" data-testid="goal-close-btn"><X size={15} style={{ color: "hsl(var(--klareet-muted))" }} /></button>
                </div>
              </div>
              <div className="k-quiet !text-sm mt-2 flex items-center gap-1.5" data-testid="goal-timeline-label">
                <CalendarClock size={13} /> {active.timeline_weeks || 8} week timeline
              </div>
              <div className="mt-2">
                {active.is_driving ? (
                  <span className="k-chip k-chip-teal !text-xs" data-testid="goal-driving-badge"><Star size={11} fill="currentColor" /> driving your app</span>
                ) : (
                  <button onClick={() => setDriving(active.id)} className="k-btn k-btn-ghost !py-1 !px-2.5 !text-xs" data-testid="goal-make-driving">
                    <Star size={11} /> Make this the driving goal
                  </button>
                )}
              </div>
            </>
          ) : (
            <div data-testid="goal-edit-card">
              <div className="k-label mb-2">edit your goal</div>
              <p className="k-quiet !text-xs mb-3" style={{ color: "hsl(28 60% 40%)" }}>
                Changing the goal or its timeline voids any signature. You will re-read it and re-sign.
              </p>
              <textarea className="k-input klareet-serif" rows={2} maxLength={200} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={{ fontSize: "1.2rem" }} data-testid="goal-edit-title" />
              <div className="mt-4"><TimelinePicker weeks={editWeeks} onPick={setEditWeeks} testidPrefix="edit-timeline" /></div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setEditing(false)} className="k-btn k-btn-ghost !text-sm" data-testid="goal-edit-cancel">Cancel</button>
                <button onClick={saveEdit} disabled={!editTitle.trim()} className="k-btn k-btn-primary !text-sm" data-testid="goal-edit-save">Save changes</button>
              </div>
            </div>
          )}

          {!editing && (
            <>
              {/* Analyze / gap */}
              {analyzing ? (
                <div className="mt-6 flex items-center gap-2 k-quiet !text-sm" data-testid="gap-analyzing">
                  <Loader2 size={15} className="animate-spin" /> Klair is reading this against your snapshot.
                </div>
              ) : active.gap_analysis ? (
                <GapAnalysis gap={active.gap_analysis} />
              ) : (
                <div className="mt-6">
                  <button onClick={() => analyze(active.id)} className="k-btn k-btn-primary !text-sm" data-testid="goal-analyze-btn">
                    <Sparkles size={14} /> Read this with Klair
                  </button>
                </div>
              )}

              {/* Off track */}
              {active.gap_analysis && active.off_track && (
                <OffTrackWarning
                  reason={active.off_track_reason}
                  acknowledged={active.off_track_acknowledged}
                  onAck={ackOffTrack}
                  busy={ackBusy}
                />
              )}

              {/* Pledge */}
              {active.gap_analysis && (
                active.signed
                  ? <PledgeReceipt goal={active} />
                  : <PledgeModule goal={active} onSigned={onSigned} />
              )}

              {/* Roadmap preview */}
              {active.gap_analysis && <GoalRoadmapPreview goal={active} base={base} />}
            </>
          )}
        </div>
      )}

      <p className="k-quiet !text-xs mt-5" data-testid="goals-footer-note">
        Your goals and pledges are private to you. Klair uses your snapshot only to help you, never to rank you.
      </p>

      <ConfirmDialog
        open={confirmClose}
        onCancel={() => setConfirmClose(false)}
        onConfirm={closeGoal}
        title="Close this goal?"
        description={active ? <>You are closing <span style={{ fontWeight: 600, color: "hsl(var(--klareet-ink))" }}>{active.title}</span>. It moves to your archive and stops showing on your goals.</> : "This goal moves to your archive."}
        consequences={[
          "It no longer shows on your active goals",
          "Any pledge and roadmap on it is set aside",
          "You can start a fresh goal in its place",
        ]}
        confirmWord="CLOSE"
        confirmLabel="Close goal"
        tone="danger"
        testId="close-goal-confirm"
      />
    </div>
  );
}

export default GoalsTab;
